/**
 * Environment variables validation for Auth configuration
 *
 * This file validates all required environment variables before the application starts.
 * It should be imported at the top of the auth configuration file.
 */

interface EnvValidationError {
  variable: string;
  message: string;
}

const REQUIRED_ENV_VARS = {
  // NextAuth configuration
  NEXTAUTH_SECRET: 'NextAuth secret key for session encryption',
  NEXTAUTH_URL: 'NextAuth URL for callbacks and redirects',

  // Auth Client Lib - Azure configuration
  PP_AUTH_LIB_REDIS_URL: 'Redis URL for session storage',
  PP_AUTH_LIB_AZURE_CLIENT_ID: 'Azure AD Client ID',
  PP_AUTH_LIB_AZURE_CLIENT_SECRET: 'Azure AD Client Secret',
  NEXT_PUBLIC_PP_AUTH_LIB_AZURE_TENANT_ID: 'Azure AD Tenant ID',

  // Auth Client Lib - API configuration
  NEXT_PUBLIC_AUTH_API_URL: 'Public Auth API URL',
  AUTH_API_URL_INTERNAL: 'Internal Auth API URL for server-side calls',
  PP_AUTH_LIB_SYSTEM_ENUM: 'System identifier enum for auth lib',
  PEOPLE_PLATFORM_API_URL: 'People Platform API URL for server-side calls',
  IAPP_API_URL: 'IAPP API URL for server-side calls (download/proxy routes)'
} as const;

const OPTIONAL_ENV_VARS = {
  PP_AUTH_LIB_AZURE_DEBUG: 'Enable Azure AD debug mode',
  PP_AUTH_LIB_AZURE_HTTP_TIMEOUT: 'HTTP timeout for Azure AD requests'
} as const;

/**
 * Validates that all required environment variables are set
 * @throws Error if any required environment variable is missing
 */
export function validateAuthEnvironment(): void {
  const errors: EnvValidationError[] = [];
  const warnings: string[] = [];

  // Check required environment variables
  Object.entries(REQUIRED_ENV_VARS).forEach(([key, description]) => {
    const value = process.env[key];

    if (!value || value.trim() === '') {
      errors.push({
        variable: key,
        message: `Missing required environment variable: ${key} (${description})`
      });
    }
  });

  // Check optional environment variables (just warnings)
  Object.entries(OPTIONAL_ENV_VARS).forEach(([key, description]) => {
    const value = process.env[key];

    if (!value || value.trim() === '') {
      warnings.push(
        `Optional environment variable not set: ${key} (${description})`
      );
    }
  });

  // Log warnings
  if (warnings.length > 0 && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('⚠️  Auth Environment Warnings:');
    // eslint-disable-next-line no-console
    warnings.forEach(warning => console.warn(`   - ${warning}`));
  }

  // Throw error if any required variable is missing
  if (errors.length > 0) {
    const errorMessage = [
      '❌ Auth Environment Validation Failed!',
      '',
      'The following required environment variables are missing:',
      ...errors.map(err => `   - ${err.message}`),
      '',
      'Please check your .env file and ensure all required variables are set.',
      'See .env.example for reference.'
    ].join('\n');

    throw new Error(errorMessage);
  }

  // Success message (only in development)
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('✅ Auth environment variables validated successfully');
  }
}

/**
 * Gets a validated environment variable value
 * @param key - The environment variable key
 * @returns The environment variable value
 * @throws Error if the variable is not set
 */
export function getRequiredEnv(key: keyof typeof REQUIRED_ENV_VARS): string {
  const value = process.env[key];

  if (!value || value.trim() === '') {
    throw new Error(
      `Required environment variable ${key} is not set. ` +
        `Description: ${REQUIRED_ENV_VARS[key]}`
    );
  }

  return value;
}

/**
 * Gets an optional environment variable value
 * @param key - The environment variable key
 * @param defaultValue - Default value if not set
 * @returns The environment variable value or default
 */
export function getOptionalEnv(
  key: keyof typeof OPTIONAL_ENV_VARS,
  defaultValue = ''
): string {
  return process.env[key] || defaultValue;
}
